import { Viewer } from "cesium";
import { Tool } from "lib/types/Tool";
import { ToolManager } from "./ToolManager";
import ScreenSpaceEventsPlugin from "./ScreenSpaceEventsPlugin";
import PositionRayPickerPlugin from "./PositionRayPickerPlugin";
import FeatureRayPickerPlugin from "./FeatureRayPickerPlugin";
import CameraPlugin from "./CameraPlugin";
import TerrainPlugin from "components/elements/CesiumViewer/core/wrapper/TerrainPlugin";
import CameraControlsPlugin from "./CameraControlsPlugin";
import TilesetPlugin from "./TilesetPlugin";
import GeoJsonPlugin from "./GeoJsonPlugin";
import RasterLayerPlugin from "./RasterLayerPlugin";
import ScansetPlugin from "./ScansetPlugin";
import CoordinatesToolPlugin from "./CoordinatesToolPlugin";
import MeasureToolPlugin from "./MeasureToolPlugin";
import DrawPolygonToolPlugin from "./DrawPolygonToolPlugin";
import PaintToolPlugin from "./PaintToolPlugin";
import TilesetElementVisibilityPlugin from "./TilesetElementVisibilityPlugin";
import ControlsPresetToolPlugin from "./ControlsPresetToolPlugin";
import OrbitPointVisualizerPlugin from "./OrbitPointVisualizerPlugin";
import GeographicMarkersPlugin from "./GeographicMarkersPlugin";
import TilesetStylingPlugin from "./TilesetStylingPlugin";
import AssetsPropertiesSearchToolPlugin from "./AssetsPropertiesSearchToolPlugin";
import AssetsPropertiesLayerCriteriaPlugin from "./AssetsPropertiesLayerCriteriaPlugin";
import CameraHistoryPlugin from "./CameraHistoryPlugin";
import LevelOfDetailPresetToolPlugin from "./LevelOfDetailPresetToolPlugin";
import AssetsPlugin from "./AssetsPlugin";
import TilesetElementRepositionPlugin from "components/elements/CesiumViewer/core/wrapper/TilesetElementRepositionPlugin";
import TransformEditorPlugin from "./TransformEditorPlugin";
import ClippingBoxToolPlugin from "./ClippingBoxToolPlugin";
import SelectedElementPlugin from "components/elements/CesiumViewer/core/wrapper/SelectedElementPlugin";
import GlobeOpacityToolPlugin from "./GlobeOpacityToolPlugin";
import SelectionToolPlugin from "./SelectionToolPlugin";

export interface AttachablePluginInterface {
  setViewer(viewer: Viewer): void;
  detachViewer(): void;
}

export default class CesiumWrapper {
  readonly toolManager: ToolManager;
  readonly plugins: {
    readonly screenSpaceEventsPlugin: ScreenSpaceEventsPlugin;
    readonly positionRayPickerPlugin: PositionRayPickerPlugin;
    readonly featureRayPickerPlugin: FeatureRayPickerPlugin;
    readonly cameraPlugin: CameraPlugin;
    readonly terrainPlugin: TerrainPlugin;
    readonly cameraControlsPlugin: CameraControlsPlugin;
    readonly tilesetPlugin: TilesetPlugin;
    readonly tilesetStylingPlugin: TilesetStylingPlugin;
    readonly tilesetElementVisibilityPlugin: TilesetElementVisibilityPlugin;
    readonly geoJsonPlugin: GeoJsonPlugin;
    readonly scansetPlugin: ScansetPlugin;
    readonly rasterLayerPlugin: RasterLayerPlugin;
    readonly assetsPlugin: AssetsPlugin;
    readonly geographicMarkersPlugin: GeographicMarkersPlugin;
    readonly orbitPointVisualizerPlugin: OrbitPointVisualizerPlugin;
    readonly coordinatesToolPlugin: CoordinatesToolPlugin;
    readonly measureToolPlugin: MeasureToolPlugin;
    readonly drawPolygonToolPlugin: DrawPolygonToolPlugin;
    readonly paintToolPlugin: PaintToolPlugin;
    readonly controlsPresetPlugin: ControlsPresetToolPlugin;
    readonly assetsPropertiesSearchToolPlugin: AssetsPropertiesSearchToolPlugin;
    readonly assetsPropertiesLayerCriteriaPlugin: AssetsPropertiesLayerCriteriaPlugin;
    readonly cameraHistoryPlugin: CameraHistoryPlugin;
    readonly levelOfDetailPresetToolPlugin: LevelOfDetailPresetToolPlugin;
    readonly tilesetElementRepositionPlugin: TilesetElementRepositionPlugin;
    readonly transformEditorPlugin: TransformEditorPlugin;
    readonly clippingBoxToolPlugin: ClippingBoxToolPlugin;
    readonly selectedElementPlugin: SelectedElementPlugin;
    readonly selectionToolPlugin: SelectionToolPlugin;
    readonly globeOpacityToolPlugin: GlobeOpacityToolPlugin;
  };

  constructor() {
    const screenSpaceEventsPlugin = new ScreenSpaceEventsPlugin();
    const cameraPlugin = new CameraPlugin();
    const positionRayPickerPlugin = new PositionRayPickerPlugin(
      screenSpaceEventsPlugin,
      cameraPlugin,
    );
    const featureRayPickerPlugin = new FeatureRayPickerPlugin(
      screenSpaceEventsPlugin,
    );
    const tilesetPlugin = new TilesetPlugin();
    const tilesetStylingPlugin = new TilesetStylingPlugin(tilesetPlugin);
    const geoJsonPlugin = new GeoJsonPlugin();
    const scansetPlugin = new ScansetPlugin();
    const rasterLayerPlugin = new RasterLayerPlugin();
    const cameraControlsPlugin = new CameraControlsPlugin();
    const terrainPlugin = new TerrainPlugin();
    const transformEditorPlugin = new TransformEditorPlugin(
      screenSpaceEventsPlugin,
      positionRayPickerPlugin,
    );
    const assetsPlugin = new AssetsPlugin(
      tilesetPlugin,
      geoJsonPlugin,
      rasterLayerPlugin,
      scansetPlugin,
      cameraPlugin,
      tilesetStylingPlugin,
    );
    const selectedElementPlugin = new SelectedElementPlugin(
      tilesetPlugin,
      tilesetStylingPlugin,
    );

    this.plugins = {
      screenSpaceEventsPlugin,
      positionRayPickerPlugin,
      featureRayPickerPlugin,
      cameraControlsPlugin,
      controlsPresetPlugin: new ControlsPresetToolPlugin(cameraControlsPlugin),
      orbitPointVisualizerPlugin: new OrbitPointVisualizerPlugin(
        cameraControlsPlugin,
      ),
      cameraPlugin,
      terrainPlugin,
      tilesetPlugin,
      tilesetStylingPlugin,
      tilesetElementVisibilityPlugin: new TilesetElementVisibilityPlugin(
        tilesetStylingPlugin,
        featureRayPickerPlugin,
      ),
      geoJsonPlugin,
      rasterLayerPlugin,
      scansetPlugin,
      assetsPlugin,
      geographicMarkersPlugin: new GeographicMarkersPlugin(
        screenSpaceEventsPlugin,
        cameraPlugin,
      ),
      coordinatesToolPlugin: new CoordinatesToolPlugin(positionRayPickerPlugin),
      measureToolPlugin: new MeasureToolPlugin(
        positionRayPickerPlugin,
        cameraPlugin,
      ),
      drawPolygonToolPlugin: new DrawPolygonToolPlugin(positionRayPickerPlugin),
      paintToolPlugin: new PaintToolPlugin(
        tilesetStylingPlugin,
        featureRayPickerPlugin,
      ),
      assetsPropertiesSearchToolPlugin: new AssetsPropertiesSearchToolPlugin(
        tilesetStylingPlugin,
      ),
      assetsPropertiesLayerCriteriaPlugin:
        new AssetsPropertiesLayerCriteriaPlugin(tilesetStylingPlugin),
      cameraHistoryPlugin: new CameraHistoryPlugin(),
      levelOfDetailPresetToolPlugin: new LevelOfDetailPresetToolPlugin(),
      transformEditorPlugin: transformEditorPlugin,
      tilesetElementRepositionPlugin: new TilesetElementRepositionPlugin(
        tilesetPlugin,
        tilesetStylingPlugin,
        featureRayPickerPlugin,
        transformEditorPlugin,
      ),
      selectedElementPlugin,
      selectionToolPlugin: new SelectionToolPlugin(
        featureRayPickerPlugin,
        selectedElementPlugin,
      ),
      globeOpacityToolPlugin: new GlobeOpacityToolPlugin(terrainPlugin),
      clippingBoxToolPlugin: new ClippingBoxToolPlugin(
        tilesetPlugin,
        assetsPlugin,
        screenSpaceEventsPlugin,
      ),
    };

    this.toolManager = new ToolManager(Tool.select);
    this.registerTools();
  }

  setViewer(viewer: Viewer) {
    this.getPlugins().forEach((p) => p.setViewer(viewer));
  }

  detachViewer() {
    this.getPlugins().forEach((p) => p.detachViewer());
  }

  private getPlugins() {
    return this.plugins
      ? Object.values<AttachablePluginInterface>(this.plugins)
      : [];
  }

  private registerTools() {
    this.toolManager.register(Tool.select, this.plugins.selectionToolPlugin);
    this.toolManager.register(
      Tool.opacity,
      this.plugins.globeOpacityToolPlugin,
    );
    this.toolManager.register(Tool.measure, this.plugins.measureToolPlugin);
    this.toolManager.register(
      Tool.coordinates,
      this.plugins.coordinatesToolPlugin,
    );
    this.toolManager.register(Tool.paint, this.plugins.paintToolPlugin);
    this.toolManager.register(
      Tool.clippingBox,
      this.plugins.clippingBoxToolPlugin,
    );
    this.toolManager.register(
      Tool.drawPolygon,
      this.plugins.drawPolygonToolPlugin,
    );
    this.toolManager.register(
      Tool.visibility,
      this.plugins.tilesetElementVisibilityPlugin,
    );
    this.toolManager.register(
      Tool.search,
      this.plugins.assetsPropertiesSearchToolPlugin,
    );
    this.toolManager.register(
      Tool.elementReposition,
      this.plugins.tilesetElementRepositionPlugin,
    );
    this.toolManager.register(
      Tool.levelOfDetailPreset,
      this.plugins.levelOfDetailPresetToolPlugin,
    );
    this.toolManager.register(
      Tool.controlsPreset,
      this.plugins.controlsPresetPlugin,
    );
  }
}
